import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, Plus, Trash2, UserX, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

const LEVELS = ["awareness", "practitioner", "expert"];

export default function TrainingMatrix() {
  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2"><GraduationCap className="w-3 h-3" /> Training</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            Role-based Training Matrix
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            ISO 13485 §6.2 aligned. Define which courses are required for each role, then surface training gaps
            across your team.
          </p>
        </div>
        <Tabs defaultValue="matrix">
          <TabsList data-testid="training-tabs">
            <TabsTrigger value="matrix" data-testid="tab-matrix">Matrix</TabsTrigger>
            <TabsTrigger value="gaps" data-testid="tab-gaps"><UserX className="w-4 h-4 mr-1" /> Gaps</TabsTrigger>
            <TabsTrigger value="renewals" data-testid="tab-renewals"><RefreshCw className="w-4 h-4 mr-1" /> Renewals</TabsTrigger>
          </TabsList>
          <TabsContent value="matrix"><MatrixTab /></TabsContent>
          <TabsContent value="gaps"><GapsTab /></TabsContent>
          <TabsContent value="renewals"><RenewalsTab /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function MatrixTab() {
  const [data, setData] = useState({ matrix: [], roles: [], courses: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: "", course_id: "", competency_level: "practitioner", required: true });

  const load = useCallback(() => {
    api.get("/training/matrix").then(({ data }) => setData(data))
      .catch((e) => logger.warn("Matrix load:", e?.response?.status));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/training/matrix", form);
      toast.success("Matrix row saved.");
      setOpen(false);
      setForm({ role: "", course_id: "", competency_level: "practitioner", required: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const remove = async (role, course_id) => {
    if (!window.confirm(`Remove ${course_id} requirement from ${role}?`)) return;
    try {
      await api.delete(`/training/matrix?role=${encodeURIComponent(role)}&course_id=${encodeURIComponent(course_id)}`);
      toast.success("Removed."); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  // Group matrix by role for the grid view
  const byRole = data.roles.reduce((acc, r) => { acc[r] = []; return acc; }, {});
  data.matrix.forEach((m) => { (byRole[m.role] = byRole[m.role] || []).push(m); });

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">
          {data.matrix.length} matrix row{data.matrix.length === 1 ? "" : "s"} · {data.roles.length} roles · {data.courses.length} courses
        </div>
        <Button data-testid="new-matrix-btn" onClick={() => setOpen(true)} disabled={data.courses.length === 0}>
          <Plus className="w-4 h-4 mr-1" /> Add requirement
        </Button>
      </div>
      {data.courses.length === 0 && (
        <div className="vq-card p-4 border-l-4 border-l-amber-500 bg-amber-50 text-xs text-amber-900">
          No training courses exist yet. Create courses under the QMS Training module first, then define the matrix here.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(byRole).map(([role, rows]) => (
          <div key={role} className="vq-card overflow-hidden" data-testid={`matrix-role-${role.replace(/\s/g, "-")}`}>
            <div className="p-3 border-b bg-slate-50 font-display font-bold text-sm text-slate-900">{role}</div>
            {rows.length === 0 ? (
              <div className="p-4 text-xs text-slate-400">No required trainings for this role.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {rows.map((m) => (
                    <tr key={`${m.role}-${m.course_id}`} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{m.course_title}</div>
                        <div className="text-xs text-slate-500 font-mono">{m.course_id}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <Badge variant="secondary">{m.competency_level}</Badge>
                        {m.required && <Badge className="ml-1">required</Badge>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" data-testid={`del-${m.role}-${m.course_id}`}
                                onClick={() => remove(m.role, m.course_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add training requirement</DialogTitle>
            <DialogDescription>Assign a course as required for a role at a target competency level.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="matrix-role"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Course</Label>
              <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                <SelectTrigger data-testid="matrix-course"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {data.courses.map((c) => <SelectItem key={c.course_id} value={c.course_id}>{c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Competency level</Label>
              <Select value={form.competency_level} onValueChange={(v) => setForm({ ...form, competency_level: v })}>
                <SelectTrigger data-testid="matrix-level"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input data-testid="matrix-required" type="checkbox" checked={form.required}
                     onChange={(e) => setForm({ ...form, required: e.target.checked })} />
              Required (blocks gap-analyzer reporting a gap)
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="matrix-submit" onClick={submit} disabled={!form.role || !form.course_id}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RenewalsTab() {
  // Configurable renewal window — GxP roles often need 12-month refreshers,
  // awareness training 24. Keep it a slider so the user picks their policy.
  const [months, setMonths] = useState(12);
  const [data, setData] = useState({ count: 0, renewals_due: [], threshold_months: 12 });
  const [loading, setLoading] = useState(true);
  const [reassigning, setReassigning] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/training/renewals?threshold_months=${months}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Renewals load failed."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [months]);

  const sopUpdatedCount = (data.renewals_due || []).filter((r) => r.sop_updated).length;

  const reassignSopUpdated = async () => {
    if (!sopUpdatedCount) return;
    setReassigning(true);
    try {
      const { data: res } = await api.post("/training/reassign-sop-updated", {});
      if (res.created_count === 0 && res.skipped_existing) {
        toast.info(`Already re-assigned — ${res.skipped_existing} open assignment${res.skipped_existing === 1 ? "" : "s"} already in progress`);
      } else if (res.created_count === 0) {
        toast.info("Nothing to re-assign — no completed assignments flagged SOP updated");
      } else {
        toast.success(`Re-assigned ${res.created_count} training${res.created_count === 1 ? "" : "s"} for updated SOPs`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-assign failed");
    } finally {
      setReassigning(false);
    }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="vq-card p-4 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
          <div className="font-bold text-2xl text-amber-700" data-testid="renewals-count">{data.count}</div>
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-slate-900">Trainings due for renewal</div>
          <div className="text-xs text-slate-500 mt-1">
            Completions older than <b>{months}</b> month{months === 1 ? "" : "s"}. Adjust the threshold to match your policy — VitaQMS does not enforce a specific cadence.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap" data-testid="renewals-threshold">
          {sopUpdatedCount > 0 && (
            <Button
              data-testid="reassign-sop-updated-btn"
              onClick={reassignSopUpdated}
              disabled={reassigning}
              size="sm"
              className="bg-[#0055FF] hover:bg-[#0044cc] text-white font-semibold"
              title={`Create fresh assignments for the ${sopUpdatedCount} completion${sopUpdatedCount === 1 ? "" : "s"} whose linked SOP has been revised. Skips assignees who already have an open assignment.`}
            >
              {reassigning ? "Re-assigning…" : `Re-assign ${sopUpdatedCount} SOP-updated`}
            </Button>
          )}
          <Label className="text-xs text-slate-500">Months</Label>
          <select
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value, 10))}
            className="border border-slate-300 rounded px-2 py-1 text-sm font-mono"
            data-testid="renewals-months-select"
          >
            {[6, 12, 18, 24, 36].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Course</th>
              <th className="text-left px-3 py-2">Linked SOP</th>
              <th className="text-left px-3 py-2">Completed</th>
              <th className="text-left px-3 py-2">Age</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="renewals-tbody">
            {loading && <tr><td colSpan="6" className="text-center px-3 py-6 text-slate-400">Loading…</td></tr>}
            {!loading && data.renewals_due.length === 0 && (
              <tr><td colSpan="6" className="text-center px-3 py-6 text-emerald-700">
                Every completed training is within the {months}-month window. 🎉
              </td></tr>
            )}
            {data.renewals_due.map((r) => (
              <tr key={r.assignment_id} className="border-t border-slate-100" data-testid={`renewal-row-${r.assignment_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.assignee_email}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.course_id}</td>
                <td className="px-3 py-2 text-xs">
                  <LinkedSopCell row={r} />
                </td>
                <td className="px-3 py-2 text-xs">{(r.completed_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <Badge variant={r.months_since_completion > months * 1.5 ? "destructive" : "secondary"}>
                    {r.months_since_completion} mo
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`renewal-cert-${r.assignment_id}`}
                    onClick={() => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/training/certificate/${r.assignment_id}`;
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    className="text-xs border-slate-300"
                    title="Download the original completion certificate"
                  >
                    Certificate ↓
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinkedSopCell({ row }) {
  const title = row.linked_document_title;
  if (!row.linked_document_id) {
    return <span className="text-slate-400 text-[10px] font-mono">—</span>;
  }
  const signedVer = row.linked_document_version_signed || row.linked_document_version_at_assignment;
  const currentVer = row.linked_document_current_version;
  return (
    <div className="flex items-start gap-2" data-testid={`renewal-sop-${row.assignment_id}`}>
      <div>
        <div className="text-slate-800 font-medium truncate max-w-[220px]" title={title || row.linked_document_id}>
          {title || row.linked_document_id}
        </div>
        <div className="text-[10px] font-mono text-slate-500">
          signed v{signedVer || "?"}{currentVer && currentVer !== signedVer ? ` · current v${currentVer}` : ""}
        </div>
      </div>
      {row.sop_updated && (
        <Badge
          variant="destructive"
          className="text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-300"
          data-testid={`renewal-sop-updated-${row.assignment_id}`}
          title="Linked SOP has been revised since this training was signed. Retraining recommended."
        >
          SOP updated
        </Badge>
      )}
    </div>
  );
}

function GapsTab() {
  const [gaps, setGaps] = useState({ total_users_with_gaps: 0, gaps: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/training/gaps").then(({ data }) => setGaps(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gap load failed."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="mt-6 space-y-4">
      <div className="vq-card p-4 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
          <div className="font-bold text-2xl text-red-700" data-testid="gap-users-count">{gaps.total_users_with_gaps}</div>
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-slate-900">Users with training gaps</div>
          <div className="text-xs text-slate-500 mt-1">Required-course completions missing per user, based on the current matrix.</div>
        </div>
      </div>

      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Roles</th>
              <th className="text-left px-3 py-2">Missing trainings</th>
            </tr>
          </thead>
          <tbody data-testid="gaps-tbody">
            {loading && <tr><td colSpan="3" className="text-center px-3 py-6 text-slate-400">Loading…</td></tr>}
            {!loading && gaps.gaps.length === 0 && (
              <tr><td colSpan="3" className="text-center px-3 py-6 text-emerald-700">No training gaps. 🎉</td></tr>
            )}
            {gaps.gaps.map((g) => (
              <tr key={g.email} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <div className="font-medium">{g.name || g.email}</div>
                  <div className="text-xs text-slate-500">{g.email}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {(g.roles || []).map((r) => <Badge key={r} variant="secondary" className="mr-1">{r}</Badge>)}
                  {(!g.roles || g.roles.length === 0) && <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  <Badge variant="destructive">{g.missing_count}</Badge>
                  <span className="ml-2 text-slate-600">
                    {(g.missing || []).map((m) => m.course_id).join(", ")}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
